import { enhancedApi as generatedBillingApi } from '@store/generated/billing';
import { enhancedApi as claimsApi } from '@store/generated/claims';

/**
 * Feature-specific tag types for cache management
 */
const BILLING_TAG_TYPES = [
  'Claim',
  'ClaimsList',
  'BillingEncounter',
  'BillingEncountersList',
  'ClaimPdf',
  'FeeSchedule',
  // Cross-feature: invalidate encounter cache when claims change
  'Encounter',
] as const;

/**
 * Claims API - extends the generated claims OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
const extendedClaimsApi = claimsApi
  .enhanceEndpoints({
    addTagTypes: BILLING_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with cache tags
      getV2ProviderClaims: {
        providesTags: result =>
          result?.claims
            ? [
                ...result.claims.map(claim => ({
                  type: 'Claim' as const,
                  id: claim.id ?? 'unknown',
                })),
                { type: 'ClaimsList' as const, id: 'LIST' },
              ]
            : [{ type: 'ClaimsList' as const, id: 'LIST' }],
      },

      postV2ProviderClaims: {
        invalidatesTags: [
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingEncountersList', id: 'LIST' },
          { type: 'Encounter' },
        ],
      },

      getV2ProviderClaimsByClaimId: {
        providesTags: (_result, _error, arg) => [{ type: 'Claim', id: arg.claimId }],
      },

      putV2ProviderClaimsByClaimId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
        ],
      },

      deleteV2ProviderClaimsByClaimId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
        ],
      },

      getV2ProviderClaimsByClaimIdPdf: {
        providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf', id: arg.claimId }],
      },

      postV2ProviderClaimsByClaimIdScrub: {
        // Don't invalidate Claim tag - scrub doesn't modify the claim on server
        // This prevents unnecessary refetch that triggers autosave
        invalidatesTags: () => [],
        // Update the claim in cache with scrub results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the scrub results
            dispatch(
              billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                { claimId: arg.claimId }, 
                () => {
                  // The scrub response is the updated claim
                  return data;
                }
              )
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      },

      postV2ProviderClaimsByClaimIdRebuild: {
        // Invalidate claim and list since rebuild regenerates the claim
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' }
          ] : [],
      },

      postV2ProviderClaimsByClaimIdSubmit: {
        // Invalidate claim and list since submit changes claim status
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' }
          ] : [],
        // Update the claim in cache with submit results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the submit results
            if (data?.claim) {
              dispatch(
                billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                  { claimId: arg.claimId }, 
                  () => {
                    // The submit response contains the updated claim
                    return data.claim;
                  }
                )
              );
            }
          } catch {
            // Error handled by mutation hook
          }
        },
      },
    },
  });

/**
 * Fee Schedule API - extends the generated billing OpenAPI client
 * Handles baseline CPT rates for the organization
 */
const extendedBillingApi = generatedBillingApi
  .enhanceEndpoints({
    addTagTypes: ['FeeSchedule', 'BillingEncounter', 'BillingEncountersList'],
    endpoints: {
      // Encounters list
      getV2ProviderBillingEncounters: {
        providesTags: result =>
          result?.encounters
            ? [
                ...result.encounters.map(({ encounter_id }) => ({
                  type: 'BillingEncounter' as const,
                  id: encounter_id,
                })),
                { type: 'BillingEncountersList' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingEncountersList' as const, id: 'LIST' }],
      },

      // Single encounter detail
      getV2ProviderBillingEncountersByEncounterId: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      // GET baseline rates
      getV2ProviderBillingFeeSchedulesBaseline: {
        providesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // PUT baseline rates (bulk upsert)
      putV2ProviderBillingFeeSchedulesBaseline: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // DELETE single baseline rate by CPT code
      deleteV2ProviderBillingFeeSchedulesBaselineByCptCode: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },
    },
  });

/**
 * Manually injected endpoints not yet in the OpenAPI spec
 */
const injectedClaimsApi = extendedClaimsApi.injectEndpoints({
  endpoints: build => ({
    getStediPdf: build.query<
      import('@store/generated/claims').ClaimPdfGenerateResponse,
      { claimId: string; background?: boolean }
    >({
      query: ({ claimId, background = true }) => ({
        url: `/v2/provider/claims/${claimId}/stedi-pdf`,
        params: { background },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf' as const, id: `stedi-${arg.claimId}` }],
    }),
    batchSubmitClaims: build.mutation<
      { submitted: Array<{ claim_id: string }>; failed: Array<{ claim_id: string; error: string }> },
      { batchSubmitClaimsRequest: { claim_ids: string[] } }
    >({
      query: ({ batchSubmitClaimsRequest }) => ({
        url: '/v2/provider/claims/batch-submit',
        method: 'POST',
        body: batchSubmitClaimsRequest,
      }),
      invalidatesTags: [{ type: 'ClaimsList' as const, id: 'LIST' }],
    }),
  }),
});

// Export hooks with user-friendly names
export const {
  // Claims queries and mutations
  useGetV2ProviderClaimsQuery: useGetClaimsQuery,
  useLazyGetV2ProviderClaimsQuery: useLazyGetClaimsQuery,
  usePostV2ProviderClaimsMutation: useCreateClaimMutation,
  useGetV2ProviderClaimsByClaimIdQuery: useGetClaimByIdQuery,
  useLazyGetV2ProviderClaimsByClaimIdQuery: useLazyGetClaimByIdQuery,
  usePutV2ProviderClaimsByClaimIdMutation: useUpdateClaimMutation,
  useDeleteV2ProviderClaimsByClaimIdMutation: useDeleteClaimMutation,

  // PDF generation
  useGetV2ProviderClaimsByClaimIdPdfQuery: useGetClaimPdfQuery,
  useLazyGetV2ProviderClaimsByClaimIdPdfQuery: useLazyGetClaimPdfQuery,
  useLazyGetStediPdfQuery,

  // Claim scrubbing
  usePostV2ProviderClaimsByClaimIdScrubMutation: useClaimScrubMutation,

  // Claim rebuild
  usePostV2ProviderClaimsByClaimIdRebuildMutation: useClaimRebuildMutation,

  // Claim submission
  usePostV2ProviderClaimsByClaimIdSubmitMutation: useClaimSubmitMutation,

  // Batch submission
  useBatchSubmitClaimsMutation,
} = injectedClaimsApi;

// Export encounter and fee schedule hooks with user-friendly names
export const {
  // Billing encounters list
  useGetV2ProviderBillingEncountersQuery: useGetBillingEncountersQuery,
  useLazyGetV2ProviderBillingEncountersQuery: useLazyGetBillingEncountersQuery,

  // Single encounter detail
  useGetV2ProviderBillingEncountersByEncounterIdQuery: useGetEncounterDetailQuery,

  // Fee schedule queries and mutations
  useGetV2ProviderBillingFeeSchedulesBaselineQuery: useGetBaselineFeeScheduleQuery,
  usePutV2ProviderBillingFeeSchedulesBaselineMutation: useUpdateBaselineFeeScheduleMutation,
  useDeleteV2ProviderBillingFeeSchedulesBaselineByCptCodeMutation: useDeleteBaselineFeeScheduleMutation,

  // Remittances
  useGetV2ProviderRemittancesQuery: useGetProviderRemittancesQuery,
  useGetV2ProviderRemittancesByRemittanceIdQuery: useGetProviderRemittanceDetailQuery,
  useGetV2ProviderRemittancesClaimsByClaimIdSummaryQuery: useGetProviderClaimRemittanceSummaryQuery,
} = extendedBillingApi;

// Export claims API as billingApi for backward compatibility
export const billingApi = extendedClaimsApi;

// Export billing encounters API for selectors
export const billingEncountersApi = extendedBillingApi;
